import * as React from 'react';
import { ctw } from '@/common/utils/ctw';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

const badgeVariants = cva(
  'flex inline-flex items-center justify-center rounded-full cursor-default transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 px-3 py-1 gap-1',
  {
    variants: {
      variant: {
        outline: 'text-foreground',
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        info: 'bg-info/20 text-info',
        success: 'bg-success/20 text-success',
        warning: 'bg-warning/20 text-warning',
        destructive: 'bg-destructive/20 text-destructive',
        violet: 'bg-violet/20 text-violet',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

export type BadgeProps = {
  asChild?: boolean;
} & React.HTMLAttributes<HTMLDivElement> &
  BadgeVariantProps;

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, children, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp className={ctw(badgeVariants({ variant }), className)} {...props} ref={ref}>
        {children}
      </Comp>
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
